/** Typed API surface for the requester portal — Step 71. Everything rides
 *  portalFetch/portalSend (401 → the PORTAL login, never the agent one). */
import { portalFetch, portalSend } from '../../lib/portalApi';
import type { ResolvedField } from '@itsm/shared';

export interface PortalCategory {
  id: string;
  parentId: string | null;
  name: string;
}
export interface PortalItem {
  id: string;
  categoryId: string | null;
  categoryIds: string[];
  name: string;
  description: string | null;
  /** Sanitised on write; null unless the admin filled it in (Step 65). */
  descriptionHtml?: string | null;
  /** Already visibility-gated server-side — null means "not shown here". */
  cost?: string | null;
  deliveryTime?: string | null;
  icon: string | null;
  /** Step 73 — an uploaded icon, in front of the lucide name. */
  iconAttachmentId?: string | null;
  /** Step 72 — offer the "requested for someone else" control. */
  allowOnBehalf?: boolean;
  /** Step 73 — may this request carry files, and must it. */
  allowAttachments?: boolean;
  requireAttachment?: boolean;
}
export interface PortalTicketRow {
  id: string;
  number: number;
  type: 'incident' | 'service_request';
  subject: string;
  status: string | null;
  statusCategory: 'open' | 'pending' | 'resolved' | 'closed' | null;
  assigneeName: string | null;
  source: string;
  createdAt: number;
  updatedAt: number;
}
export interface PortalMessage {
  id: string;
  kind: 'requester_message' | 'public_reply';
  bodyHtml: string;
  authorName: string | null;
  mine: boolean;
  createdAt: number;
}
export interface PortalTicketDetail {
  ticket: PortalTicketRow;
  catalogueItem: {
    id: string;
    name: string;
    icon: string | null;
    iconAttachmentId?: string | null;
    description: string | null;
  } | null;
  requestFields: ResolvedField[];
  values: Record<string, unknown>;
  attachments: {
    id: string;
    filename: string;
    contentType: string;
    size: number;
    migrated: number;
    createdAt: number;
  }[];
  messages: PortalMessage[];
}

export const portalApi = {
  catalogue: () =>
    portalFetch<{
      categories: PortalCategory[];
      items: PortalItem[];
      favourites: string[];
      popular: string[];
    }>('/api/portal/catalogue'),
  setFavourite: (id: string, favourite: boolean) =>
    portalSend(`/api/portal/catalogue/items/${id}/favourite`, 'PUT', { favourite }),
  profile: () =>
    portalFetch<{
      id: string;
      email: string;
      name: string | null;
      jobTitle: string | null;
      department: string | null;
      officeLocation: string | null;
      kind: string;
    }>('/api/portal/profile'),
  updateProfile: (body: { name?: string; jobTitle?: string; department?: string; officeLocation?: string }) =>
    portalSend('/api/portal/profile', 'PATCH', body),
  kbTree: () =>
    portalFetch<{
      categories: {
        id: string;
        name: string;
        description: string | null;
        folders: { id: string; name: string; description: string | null; published: number }[];
      }[];
    }>('/api/portal/kb/tree'),
  kbFolderArticles: (folderId: string) =>
    portalFetch<{
      folder: { id: string; name: string };
      entries: { id: string; title: string; slug: string }[];
    }>(`/api/portal/kb/folders/${folderId}/articles`),
  kbArticle: (slug: string) =>
    portalFetch<{
      id: string;
      title: string;
      bodyHtml: string | null;
      slug: string;
      folderName: string;
      helpful: number;
      notHelpful: number;
      updatedAt: number;
    }>(`/api/portal/kb/articles/${slug}`),
  kbVote: (slug: string, helpful: boolean) =>
    portalSend(`/api/portal/kb/articles/${slug}/vote`, 'POST', { helpful }),
  search: (q: string, entity?: string) =>
    portalFetch<{
      tickets: {
        entries: { id: string; number: number; type: string; subject: string }[];
        total: number;
      } | null;
      items: {
        entries: { id: string; name: string; description: string | null; icon: string | null }[];
        total: number;
      } | null;
      articles: {
        entries: { id: string; title: string; slug: string; folderName: string }[];
        total: number;
      } | null;
    }>(`/api/portal/search?q=${encodeURIComponent(q)}${entity ? `&entity=${entity}` : ''}`),
  itemForm: (id: string) =>
    portalFetch<{ item: PortalItem; fields: ResolvedField[]; prefill?: Record<string, string> }>(
      `/api/portal/catalogue/items/${id}/form`,
    ),
  submit: (
    id: string,
    values: Record<string, unknown>,
    /** Step 72 — who it is FOR, when that is not the person filling it in. */
    onBehalfOf?: { email: string; name?: string },
    /** Step 73 — ids of files already uploaded through the portal's door. */
    attachmentIds?: string[],
  ) =>
    portalSend<{ id: string; number: number; ref: string }>(
      `/api/portal/catalogue/items/${id}/request`,
      'POST',
      {
        values,
        ...(onBehalfOf ? { onBehalfOf } : {}),
        ...(attachmentIds?.length ? { attachmentIds } : {}),
      },
    ),
  myTickets: () => portalFetch<{ entries: PortalTicketRow[] }>('/api/portal/tickets'),
  ticket: (id: string) => portalFetch<PortalTicketDetail>(`/api/portal/tickets/${id}`),
  reply: (id: string, bodyHtml: string) =>
    portalSend<{ ok: true }>(`/api/portal/tickets/${id}/reply`, 'POST', { bodyHtml }),
};
